import { useEffect, useContext, useRef } from 'react';
import { ImpressionContext, impressionGlobalData } from './ImpressionContext';
import {
  ROOT_MARGIN,
  THRESHOLD,
  ROOT,
  IMPRESSION_WAIT_TIME,
} from '../utils/constants';
import { impressionAnalytics } from '../utils/impressionAnalytics';
import { impressionFallbackCheck } from '../utils/impressionFallbackCheck';
import { impressionStringRefinement } from '../utils/impressionStringRefinement';

const observerCallback = (entries) => {
  // eslint-disable-next-line no-restricted-syntax
  entries.forEach((entry) => {
    let componentData = impressionGlobalData.impressionMap.get(entry.target);

    if (!entry.isIntersecting) {
      clearTimeout(componentData.timeout);
      componentData.visibleSince = 0;
    }

    let entryIsVisible = entry.isIntersecting && !componentData.visibleSince;

    const executeImpression = () => {
      // eslint-disable-next-line max-len
      let impressed = `${componentData.container.component}-${componentData.container.name}-${componentData.container.id}-${componentData.impression.component}-${componentData.impression.name}-${componentData?.impression?.position}-${componentData.impression.id}`;
      impressionGlobalData.observer.unobserve(entry.target);
      impressionGlobalData.impressionMap.delete(entry.target);
      if (componentData.name === impressionGlobalData.impressionMap.name) {
        impressionGlobalData.impressionMap.name = 'Page';
        impressionGlobalData.impressionMap.id = '';
      }
      if (impressionGlobalData.impressed[impressed]) return; // preventing re-impression
      impressionGlobalData.impressed[impressed] = true; // this is to update a global map of what was impressed
      impressionAnalytics(componentData);
    };

    if (entryIsVisible) {
      componentData.visibleSince = entry.time;
      componentData.timeout = setTimeout(() => {
        executeImpression();
      }, IMPRESSION_WAIT_TIME);
    }
  });
};

const useImpression = (props) => {
  const ref = useRef(null); // <-- Hooks violation, but only if Provider used improperly.

  const impressionRef = props?.ref ? props.ref : ref;

  let { data, parent } = useContext(ImpressionContext);
  // this ^^^ is taking the object from impression context and
  // using it as a template to bring in values set in the data object
  // of the ImpressionProvider, or useImpression Hook.

  // allows the user to skip using multiple ImpressionProviders by passing in their own props to useImpression hook
  if (props) {
    parent = data;
    data = props.data;
  }
  impressionFallbackCheck(data, parent);

  impressionStringRefinement(data, parent);

  // clickID structure provided by Analytics request
  const clickID = `${parent.name}-${data.position}-${parent?.id}-${data.id}`;
  // eslint-disable-next-line max-len
  let impressed = impressionGlobalData.impressed[`${parent.component}-${parent.name}-${parent.id}-${data.component}-${data.name}-${data.position}-${data.id}`];

  useEffect(() => {
    if (!impressionRef?.current || impressionGlobalData.impressed[impressed]) return;

    impressionGlobalData.impressionMap.set(impressionRef.current, {
      impression: data,
      clickID,
      container: parent,
    });

    if (!impressionGlobalData.observer) {
      impressionGlobalData.observer = new IntersectionObserver(
        observerCallback,
        {
          rootMargin: ROOT_MARGIN,
          threshold: THRESHOLD,
          root: ROOT,
        }
      );
    }
    impressionGlobalData.observer.observe(impressionRef.current);
  });
  return { ref: impressionRef, clickID };
};

export { useImpression };
